import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What is dividend growth rate (DGR)?',
    a: 'Dividend Growth Rate (DGR) is the annualised rate at which a company has raised its dividend over a measured period. The formula: ((latest_dividend ÷ starting_dividend) ^ (1 ÷ years)) − 1. A stock paying $1.00 five years ago and $1.61 today has a 5-year DGR of ((1.61÷1.00)^(1÷5)) − 1 = ~10%/yr. DiviDrip computes 3-year and 5-year DGR for every dividend-paying stock and surfaces them inside the Stock Modal’s Triangle tab.',
  },
  {
    q: 'Why is DGR better than yield for predicting future income?',
    a: 'Yield is a snapshot of today; DGR is a vector pointing into the future. A 3% yield growing at 10%/yr doubles every seven years; a 5% yield with 0% growth is dead money. After 15 years, the 3%-grower out-pays the static 5%-er on every $10K invested. The longer your timeline, the more DGR matters and the less the headline yield matters. This is the Yield-on-Cost effect in action — see the "Yield on Cost" guide for the full math.',
  },
  {
    q: 'What is a "good" dividend growth rate?',
    a: 'Sector context matters, but as a rule: 8–12%/yr is the sweet spot for Dividend Aristocrat-class names (Coca-Cola long-term ~7%, Procter & Gamble ~5%, Johnson & Johnson ~6%, Microsoft ~10%, Visa ~15%). Utilities deliver 3–6%. REITs vary widely (Realty Income ~3% steady; specialty REITs higher). Above 15% is unsustainable beyond a few years unless the company is still in early dividend life (recent initiators).',
  },
  {
    q: 'What kills a high DGR?',
    a: 'Three things: (1) the payout ratio creeping toward 100% — you cannot grow a dividend you cannot afford; (2) declining free cash flow; (3) a major recession. The 2008–2009 financial crisis ended many bank DGR streaks; 2020 ended many travel/REIT streaks. Long DGR streaks like Coca-Cola’s 62 years are valuable precisely because they survived these stress tests.',
  },
  {
    q: 'How does DiviDrip surface dividend growth?',
    a: 'Two ways. First, the Stock Modal’s Triangle tab shows the 3y / 5y CAGR alongside the dividend safety score — fast growers get higher scores. Second, the Streak Risk Chart on the Triangle tab plots annual dividend per share over time so you can see whether growth is steady (good), accelerating (great), decelerating (warning), or stair-stepped from special dividends (math distorted — see the "Special Dividends in CAGR Math" guide).',
  },
  {
    q: 'Does dividend growth always mean stock-price growth?',
    a: 'Not in any single year. Over a decade, yes — a growing dividend usually drags the share price up because the yield-to-buyer compresses. If a $100 stock pays $3.00 today and management raises it to $5.00 over 5 years, holding yield constant at 3% implies a $166 stock price. This is why "Dividend Growth Investing" is a total-return strategy, not just an income one.',
  },
];

export default function LearnDividendGrowthRate() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="Dividend Growth Rate (DGR) Explained — The Metric That Beats Yield (2026) | DiviDrip"
      description="Dividend Growth Rate (DGR) predicts future income better than headline yield. The formula, the safe ranges, why a 3%-grower beats a 5%-stagnant payer over 15 years, and how to read DGR inside DiviDrip."
      canonical="https://twylightcrow.com/learn/dividend-growth-rate"
      guidePath="/learn/dividend-growth-rate"
      sectionLabel="Metrics & Evaluation"
      sectionAnchor="metrics"
      kicker="Learn · Yield metrics"
      h1="Dividend Growth Rate — The Metric That Predicts Future Income"
      datePublished="2026-06-14"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/yield-on-cost', label: 'Yield on cost — your real long-term yield' },
        { to: '/learn/payout-ratio', label: 'The payout ratio — your first safety check' },
        { to: '/learn/dividend-triangle', label: 'Reading the Dividend Triangle score' },
        { to: '/learn/special-dividends-in-cagr-math', label: 'Special dividends and CAGR distortion' },
        { to: '/learn/five-numbers-to-check', label: 'The 5 numbers I check before any buy' },
        { to: '/learn/dividend-kings-vs-aristocrats-vs-achievers', label: 'Kings vs Aristocrats vs Achievers' },
        { to: '/learn/boring-is-beautiful', label: 'Why dividend growers beat glamour stocks' },
      ]}
    >
      <p>
        Most beginners filter dividend stocks by <em>yield</em>. The pros filter
        by <strong>dividend growth rate</strong>. Yield is a snapshot of today.
        DGR is a vector pointing into the future, and over any timeline longer
        than five years it&apos;s a far stronger predictor of how much income
        you&apos;ll actually collect.
      </p>
      <p>
        This guide shows you the formula, the math that makes a 3%-grower beat
        a 5%-stagnant payer over 15 years, and how to read DGR for any stock
        inside DiviDrip in 10 seconds.
      </p>

      <h2>The formula</h2>
      <p>
        <code>DGR = ((latest_dividend &divide; starting_dividend) ^ (1 &divide; years)) &minus; 1</code>
      </p>
      <p>Worked example: Microsoft (MSFT)</p>
      <ul>
        <li>2020 annual dividend per share: <strong>$2.04</strong></li>
        <li>2025 annual dividend per share: <strong>$3.32</strong></li>
        <li>DGR = ((3.32 &divide; 2.04) ^ (1 &divide; 5)) &minus; 1 = <strong>~10.2%/yr</strong></li>
      </ul>
      <p>
        Microsoft has compounded its dividend at roughly 10% a year for the last
        five years. If management can sustain that, your dividend doubles every
        seven years &mdash; without you adding a dollar of new capital.
      </p>

      <h2>The 15-year math: why DGR beats yield</h2>
      <p>
        Imagine two stocks, both $100 today, both invest $10,000:
      </p>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Stock</th>
            <th>Starting Yield</th>
            <th>DGR</th>
            <th>Year-1 Income</th>
            <th>Year-15 Income</th>
            <th>Total income paid over 15 yrs</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>High-Yield Stagnant</td>
            <td>5.0%</td>
            <td>0%/yr</td>
            <td>$500</td>
            <td>$500</td>
            <td>$7,500</td>
          </tr>
          <tr>
            <td>Dividend Grower</td>
            <td>3.0%</td>
            <td>10%/yr</td>
            <td>$300</td>
            <td><strong>$1,142</strong></td>
            <td><strong>$9,531</strong></td>
          </tr>
        </tbody>
      </table>
      <p>
        The grower starts paying 40% less but is paying more than 2&times; the
        stagnant payer by year 15, and has cumulatively paid out 27% more cash
        over the period. If you add the higher capital appreciation that
        usually accompanies dividend growth, the gap widens further.
      </p>
      <p>
        This is why Dividend Growth Investing (DGI) became a religion in
        certain investing circles. The math compounds quietly while
        high-yielders deliver fat starter cheques that never get bigger.
      </p>

      <h2>What counts as a &quot;good&quot; DGR?</h2>
      <table className="learn-table">
        <thead>
          <tr>
            <th>DGR Band</th>
            <th>What it looks like</th>
            <th>Examples (typical long-term ranges)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>15%+/yr</td>
            <td>Recent initiator or early-life dividend payer. Unsustainable past 3&ndash;5 years.</td>
            <td>Visa (V), Mastercard (MA), Lowe&apos;s (LOW) in early growth years</td>
          </tr>
          <tr>
            <td>8&ndash;12%/yr</td>
            <td>The sweet spot. Reliable long-term compounding.</td>
            <td>Microsoft (MSFT), Texas Instruments (TXN), Costco (COST), Apple (AAPL)</td>
          </tr>
          <tr>
            <td>4&ndash;7%/yr</td>
            <td>Mature dividend Aristocrat pace. Slower but bullet-proof.</td>
            <td>Coca-Cola (KO), Procter &amp; Gamble (PG), Johnson &amp; Johnson (JNJ)</td>
          </tr>
          <tr>
            <td>2&ndash;4%/yr</td>
            <td>Token raises just above inflation. Common in regulated utilities.</td>
            <td>Southern Company (SO), Duke (DUK), Verizon (VZ)</td>
          </tr>
          <tr>
            <td>0&ndash;2%/yr</td>
            <td>Effective stagnation. Watch the payout ratio — cut risk rising.</td>
            <td>AT&amp;T (T), some bank dividends 2008&ndash;2014</td>
          </tr>
          <tr>
            <td>Below 0%/yr</td>
            <td>Dividend already cut. Run.</td>
            <td>Most names on our <Link to="/learn/dividend-cuts-of-the-decade">cuts list</Link></td>
          </tr>
        </tbody>
      </table>

      <h2>What makes a DGR sustainable</h2>
      <p>
        A high DGR by itself is a vanity metric. A sustainable DGR rests on
        three pillars:
      </p>
      <ol>
        <li>
          <strong>A low starting payout ratio.</strong> If the company pays out
          35% of earnings today, it can raise the dividend faster than earnings
          grow. If it pays out 85%, the only way to raise the dividend is to
          grow earnings first — a much harder ask. See{' '}
          <Link to="/learn/payout-ratio">the payout ratio guide</Link>.
        </li>
        <li>
          <strong>Earnings growth that keeps pace.</strong> A dividend can outrun
          earnings for a year or two, but eventually the payout ratio binds. The
          stocks that compound dividends at 10% for 20 years are the ones whose
          earnings also grow at 8&ndash;10%.
        </li>
        <li>
          <strong>Free cash flow coverage.</strong> Net income can be smoothed by
          accounting. Free cash flow is the cash actually available to pay the
          dividend. FCF / Dividends Paid should be comfortably above 1.5x.
        </li>
      </ol>

      <h2>How DiviDrip surfaces dividend growth</h2>
      <ol>
        <li>
          <strong>Triangle Score &amp; Triangle tab</strong> &mdash; The Triangle
          tab inside the Stock Modal weighs 3-year and 5-year dividend CAGR as
          two of its three core inputs. Higher CAGR contributes to a higher
          Triangle Score (more green, fewer flags).
        </li>
        <li>
          <strong>Streak Risk Chart</strong> &mdash; Also on the Triangle tab.
          The bar chart shows annual dividend-per-share for the last decade.
          You can see at a glance whether growth is steady, accelerating,
          decelerating, or distorted by special dividends.
        </li>
        <li>
          <strong>Yield on Cost on the My Position tab</strong> &mdash; If you
          already own the stock, DiviDrip shows your Yield on Cost (annual
          dividend divided by what you originally paid). YoC grows over time
          precisely <em>because</em> of dividend growth &mdash; the metric is the
          downstream effect of DGR on your actual portfolio. See{' '}
          <Link to="/learn/yield-on-cost">Yield on Cost</Link>.
        </li>
      </ol>

      <h2>The CAGR-distortion trap</h2>
      <p>
        One-time special dividends ruin DGR math. If Costco paid a $0.92
        regular dividend in 2023 and then a $16.00 special in early 2024, the
        naive 1-year &quot;DGR&quot; reads as +1,640%. DiviDrip detects this
        outlier pattern and shows you the underlying trend instead &mdash; see{' '}
        <Link to="/learn/special-dividends-in-cagr-math">special dividends and
        CAGR math</Link> for how the Streak Risk Chart handles it.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Try it</h2>
      <p>
        Open <Link to="/dashboard">DiviDrip</Link>, search any dividend stock you
        own or are considering, and jump to the <strong>Triangle</strong> tab in
        the Stock Modal. You&apos;ll see the 3-year and 5-year dividend CAGR
        right next to the headline score, and the Streak Risk Chart will show
        you visually whether that growth is steady, accelerating, or flagging.
        Compare two stocks side-by-side in the <Link to="/triangle/compare">Triangle
        Compare</Link> view to see which one is the better future-income bet,
        regardless of which has the higher yield today.
      </p>
    </LearnPage>
  );
}
